/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { Bars2Icon } from '@heroicons/react/20/solid'
import type { Identifier, XYCoord } from 'dnd-core'
import { EditPencil } from 'iconoir-react'
import { useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useConfirmation } from '../ConfirmationContext'
import { ItemTypes } from '../sortable-dnd/ItemTypes'
import QuestionPanel from './QuestionPanel'
import { CustomQuestionType } from 'app/types/entities'

export const getFieldTypeDisplayName = (fieldType: string) => {
  switch (fieldType) {
    case `one-line`:
      return `One line`
    case `multiple-lines`:
      return `Multiple lines`
    case `checkbox`:
      return `Checkbox`
    case `opt-in`:
      return `Opt-In`
    default:
      return `Unknown`
  }
}

interface DragItem {
  index: number
  id: string
  type: string
}

export default function CustomQuestion({
  locationId,
  question,
  id,
  index,
  isMoveable,
  moveQuestion,
  onDrop,
  onQuestionDeleted,
  onQuestionUpdated,
}: {
  locationId: string
  question: CustomQuestionType
  id: string
  index: number
  isMoveable: boolean
  moveQuestion: (dragIndex: number, hoverIndex: number) => void
  onDrop: () => void
  onQuestionDeleted: (id: string) => void
  onQuestionUpdated: (question: CustomQuestionType) => void
}) {
  const [showPanel, setShowPanel] = useState(false)
  const [savedQuestion, setSavedQuestion] = useState<CustomQuestionType>(question)
  const [confirm] = useConfirmation()
  const dragRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover: (item: DragItem, monitor) => {
      if (!previewRef.current) {
        return
      }

      if (!isMoveable) {
        return
      }

      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = previewRef.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveQuestion(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop(item, monitor) {
      if (!previewRef.current) {
        return
      }

      if (!isMoveable) {
        return
      }

      onDrop()
    },
  })

  const [{ opacity }, drag, preview] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id, index }
    },
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
    canDrag: () => isMoveable,
  })

  drag(dragRef)
  drop(preview(previewRef))

  let floatingData = useFloating({
    placement: `right`,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: [`top-start`, `bottom-end`, `top-end`, `right`, `left`],
        padding: 10,
      }),
      shift({
        padding: 10,
      }),
      size({
        apply({ rects, elements, availableWidth, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${Math.min(rects.reference.width, availableWidth - 20)}px`,
            maxWidth: `${availableWidth - 20}px`,
            maxHeight: `${availableHeight - 20}px`,
          })
        },
        padding: 10,
      }),
    ],
    open: showPanel,
    onOpenChange: setShowPanel,
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context, {
      outsidePress: (event) => {
        // Don't dismiss if clicking on confirmation modal
        const target = event.target as HTMLElement
        const confirmationModal = target.closest(`[role="dialog"]`)
        return !confirmationModal
      },
    }),
  ])

  const onSaveCompleted = async (data: CustomQuestionType) => {
    setShowPanel(false)

    onQuestionUpdated(data)
    setSavedQuestion(data)
  }

  const onQuestionDelete = async (id: string) => {
    await confirm({
      title: `Delete question`,
      description: `Are you sure you want to delete this question?`,
      variant: `danger`,
    }).then(() => {
      setShowPanel(false)
      onQuestionDeleted(id)
    })
  }

  return (
    <div className="flex items-center gap-3" ref={previewRef} style={{ opacity }} data-handler-id={handlerId}>
      <div
        className="flex grow cursor-pointer items-center justify-between rounded-lg border border-default px-3 py-2.5"
        ref={floatingData.refs.setReference}
        {...getReferenceProps()}
      >
        <div className="flex items-center gap-1">
          <span
            className={`text-label-sm text-default ${savedQuestion.required ? `after:ml-0.5 after:content-['*']` : ``}`}
          >
            {savedQuestion.fieldName}
          </span>
          <div className="flex items-center gap-1.5 rounded-md border-[0.5px] border-(--fg-strong)/10 bg-(--fg-strong)/8 px-2 py-0.5">
            <span className="text-label-xs text-default">
              {savedQuestion.optIn ? `Opt-In` : getFieldTypeDisplayName(savedQuestion.fieldType)}
            </span>
          </div>
        </div>
        <EditPencil height={20} className="text-strong" />
      </div>

      <div ref={dragRef} className="cursor-grab">
        <Bars2Icon height={20} className={isMoveable ? `text-minimal` : `text-minimal/40`} />
      </div>
      {showPanel && (
        <FloatingPortal>
          <div
            className="rounded-lg border border-default bg-surface shadow-elevation-2"
            ref={floatingData.refs.setFloating}
            {...getFloatingProps()}
            style={{ ...floatingData.floatingStyles }}
          >
            <QuestionPanel
              locationId={locationId}
              question={savedQuestion}
              onSaved={onSaveCompleted}
              onQuestionDelete={onQuestionDelete}
              onCancel={() => setShowPanel(false)}
            />
          </div>
        </FloatingPortal>
      )}
    </div>
  )
}
